import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { GigsumoConstants } from 'src/app/services/GigsumoConstants';
import { TimezoneDatePipe } from 'src/app/timezone-date.pipe';
import { TruncatePipe } from 'src/app/pipe/TruncatePipe.pipe';
import { DecodeUriPipe } from 'src/app/pipe/decode-uri.pipe';
import { UnderscoreRemovePipe } from 'src/app/pipe/underscore-remove.pipe';
import { AddPipe, StripHtmlTagsPipe } from './notification-text.pipes';
import Swal from 'sweetalert2';

/** Names of the page handlers behind the inline buttons (Accept / Pay). The
 *  component never calls them itself — it emits the name and the host
 *  decides what to do. The slider passes `showActions=false` and gets the
 *  "Open notifications to respond." hint instead of the button. */
export type NotificationMessageAction =
  | 'acceptCommunityInvitation'
  | 'scalexPayment'
  | 'addOrRejectMemberToCommunity'
  | 'acceptConnection'
  | 'acceptNetworkMemberInvitation'
  | 'acceptTeamInvitation'
  | 'acceptBusinessEmployee'
  | 'acceptBusinessFollowerRequest'
  | 'opencontactmodal';

/**
 * The message body of a single notification — the exact per-notifyType copy
 * the /notifications page shows in its middle column, extracted so the
 * header slider renders the SAME text. Template is generated from the page's
 * message columns; keep the two in sync by editing this file, not the page.
 *
 * Expects the raw backend record (`notifyType`, `notifyData`, `message`, …)
 * after the page's pre-processing (notifyTime through TimezoneDatePipe,
 * relative logo paths prefixed with the photo URL).
 */
@Component({
  selector: 'app-notification-message',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    TruncatePipe,
    DecodeUriPipe,
    UnderscoreRemovePipe,
    TimezoneDatePipe,
    AddPipe,
    StripHtmlTagsPipe,
  ],
  templateUrl: './notification-message.component.html',
  styleUrls: ['./notification-message.component.scss'],
})
export class NotificationMessageComponent {
  /** Raw notification record. */
  @Input() item: any;
  /** true on the /notifications page (inline Accept/Pay buttons render);
   *  false in the slider (buttons hidden, hint shown instead). */
  @Input() showActions = false;

  /** Emits the page handler name when an inline button is clicked. */
  @Output() action = new EventEmitter<NotificationMessageAction>();
  /** Emitted on hover of an unread "plan upgraded" message (confetti on the page). */
  @Output() celebrate = new EventEmitter<void>();

  GigsumoConstant = GigsumoConstants;

  constructor(private router: Router) {}

  /** Types rendered by the generic switch block at the end of the template.
   *  Mirrors `supportedNotifyTypes` on the page. */
  private static readonly SUPPORTED_NOTIFY_TYPES = [
    'MONTHLY_ADD_ON_CREDITS',
    'MONTHLY_REFRESH_ALERT',
    'CREDIT_COSNUMED_FOR_JOBS',
    'CREDIT_REWARDED',
    'BUSINESS_PAGE_BLOCKED',
    'BUSINESS_PAGE_UNBLOCKED',
    'STRIPE_INVOICE_SUCCESS',
    'STRIPE_INVOICE_FAILURE',
    'CREDIT_COSNUMED_FOR_CANDIDATES',
    'JOBS_DOWNGRADED',
    'CANDIDATES_DOWNGRADED',
    'JOBS_DOWNGRADING_ALERT',
    'CANDIDATES_DOWNGRADING_ALERT',
    'ADMIN_ACTION_ON_REPORT',
    'FLAG_RECEIVED_ACKNOWLEDGEMENT',
    'MEMBERSHIP_UPDATED',
    'STRIPE_PAYMENT_TRANSACTION_FAILURE',
    'SUGGESTED_CANDIDATES_TO_JOB_OWNER',
    'SUGGESTED_JOBS_TO_CANDIDATE_OWNER',
    'JOB_APPLIED_TO_JOB_OWNER',
    'JOB_APPLIED_TO_CANDIDATE_OWNER',
    'SUGGESTED_CANDIDATE_REFRESH',
    'SUGGESTED_JOB_REFRESH',
    'CONTRACT_AGREEMENT_DOCUMENT_UPLOADED',
    'CONTRACT_COMPANY_DOC_CHECKLIST_CREATED',
    'CONTRACT_COMPANY_DOC_UPLOADED',
    'CONTRACT_COMPANY_DOC_VERIFIED',
    'CONTRACT_COMPANY_DOC_REJECTED',
    'CONTRACT_AGREEMENT_SIGNING_REQUESTED',
    'CONTRACT_AGREEMENT_SIGNED',
    'CONTRACT_AGREEMENT_ACCEPTED',
    'CONTRACT_AGREEMENT_REJECTED',
    'CONTRACT_WORK_ORDER_INITIATED',
    'CONTRACT_WORK_ORDER_ACCEPTED',
    'CONTRACT_WORK_ORDER_REJECTED',
    'CONTRACT_WORK_ORDER_REVISED',
    'CONTRACT_WORK_ORDER_VOIDED',
    'CONTRACT_INITIATED',
    'CONTRACT_INVOICE_SUBMITTED',
    'CONTRACT_INVOICE_UPDATED',
    'CONTRACT_INVOICE_APPROVED',
    'CONTRACT_INVOICE_REJECTED',
    'CONTRACT_INVOICE_PAID',
    'CONTRACT_PAYMENT_RECEIVED',
    'CONTRACT_INVOICE_VOIDED',
  ];

  isSupportedNotifyType(type: string): boolean {
    return NotificationMessageComponent.SUPPORTED_NOTIFY_TYPES.includes(type);
  }

  /** "a,b,c" → "a, b, c" (transfer notifications list ids in `message`). */
  formatCandidateIds(message: string): string {
    if (!message) return '';
    return message.split(',').map(id => id.trim()).join(', ');
  }

  getCandidateCount(message: string): number {
    return message ? message.split(',').filter(id => id.trim()).length : 0;
  }

  /** Tooltip text for the truncated id list. */
  addSpace(value: string): string {
    return value ? value.replace(/,/g, ', ') : '';
  }

  /** "WOID123" out of a contract work-order message. */
  extractWorkOrderId(message: string): string {
    if (!message) return '';
    const match = message.match(/WOID\d+/);
    return match ? match[0] : '';
  }

  /** Liked/Commented/Shared post links carry the post's id in notifyData,
   *  which the backend omits once the post has been deleted (there's nothing
   *  left to resolve it to). These links navigate imperatively (not via
   *  [routerLink]) so a missing id can pop up "no longer available" and stay
   *  on the notifications page — a [routerLink] on the same anchor would fire
   *  its own click handler and navigate regardless of preventDefault(). */
  handlePostLinkClick(event: Event, postId: any): void {
    event.preventDefault();
    if (!postId) {
      Swal.fire({
        title: 'Post not found',
        text: 'This post is no longer available. It may have been deleted.',
        icon: 'info',
        confirmButtonText: 'OK'
      });
      return;
    }
    this.router.navigate(['/landingPage', 'feed'], { queryParams: { postId } });
  }
}
